import { MAIN_CONTENT_ID } from '@shared/constants/ContentIdValues';
import ISearchParams from '@shared/models/ISearchParams';
import { LicenseKeyFilterTypes } from '@shared/models/organizations/LicenseKeyFilterTypes';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import AlertsContainer from './state/AlertsContainer';
import OrganizationContainer from './state/OrganizationContainer';
import useScrollToElement from './useScrollToElement';
import useStateRef from './useStateRef';

export default function useLicenseKeySearch(defaultFilter: LicenseKeyFilterTypes = 'all') {
    const { t } = useTranslation();
    const { organizationId } = useParams<{ organizationId: string }>();
    const { showError } = AlertsContainer.useContainer();
    const organizationContainer = OrganizationContainer.useContainer();
    const defaultTake = 51;
    const displayLimit = defaultTake - 1;
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedQuery, setDebouncedQuery] = useState<ISearchParams>({
        searchText: '',
        skip: 0,
        take: defaultTake,
    });
    const [searchFilteredLicenseKeys, setSearchFilteredLicenseKeys, searchFilteredLicenseKeysRef] =
        useStateRef<LicenseKeyFilterTypes>(defaultFilter);

    useScrollToElement(MAIN_CONTENT_ID, [debouncedQuery.skip]);

    function handleSearchQueryChange(value: string) {
        setSearchQuery(value);
    }

    function handleSearchFilteredLicenseKeysChange(value: LicenseKeyFilterTypes) {
        setSearchFilteredLicenseKeys(value);
    }
    const showPrevious = useMemo(() => debouncedQuery.skip && debouncedQuery.skip > 0, [debouncedQuery.skip]);
    const showNext = useMemo(
        () => organizationContainer.orgManagedLicenseKeys.length >= displayLimit,
        [organizationContainer.orgManagedLicenseKeys]
    );

    const DEBOUNCE_DELAY = 500;

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const newQuery = {
                searchText: searchQuery,
                skip: 0,
                take: defaultTake,
            };
            if (newQuery.searchText !== debouncedQuery.searchText) {
                setDebouncedQuery(newQuery);
            }
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(timeoutId);
    }, [searchQuery, searchFilteredLicenseKeys]);

    const handleNext = () => {
        const count = organizationContainer.orgManagedKeysLicenseRef.current.length;
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: displayLimit + (prev.skip ?? 0),
        }));
    };

    const handlePrevious = () => {
        const count = organizationContainer.orgManagedKeysLicenseRef.current.length;
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: Math.max(0, (prev.skip ?? 0) - displayLimit),
        }));
    };

    useEffect(() => {
        const trySearch = async () => {
            if (organizationContainer.loadingDataRef.current.licenseKeys || !organizationId) return;
            try {
                organizationContainer.handleLoadingDataChange('licenseKeys', true);
                organizationContainer.searchOrgManageLicenseKeys(
                    organizationId,
                    debouncedQuery,
                    searchFilteredLicenseKeysRef.current
                );
            } catch (e) {
                showError(t('common:license.key.error'));
            } finally {
                organizationContainer.handleLoadingDataChange('licenseKeys', false);
            }
        };
        trySearch();
    }, [debouncedQuery]);

    return {
        results: organizationContainer.orgManagedLicenseKeys,
        handleSearchQueryChange,
        searchQuery,
        searchFilteredLicenseKeys,
        handleSearchFilteredLicenseKeysChange,
        showPrevious,
        showNext,
        handleNext,
        handlePrevious,
        loadingData: organizationContainer.loadingData.licenseKeys,
    };
}
